
import React, { useEffect, useState, useMemo } from 'react';
import { FAQS, CONTACT_INFO } from '../constants';

interface FAQModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FAQModal: React.FC<FAQModalProps> = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setSearchQuery(''); // Reset search when opening
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const filteredFaqs = useMemo(() => {
    if (!searchQuery.trim()) return FAQS;
    const query = searchQuery.toLowerCase();
    return FAQS.filter(
      faq => 
        faq.question.toLowerCase().includes(query) || 
        faq.answer.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
      <div 
        className="absolute inset-0 bg-primary/20 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />
      
      <div className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-[3rem] shadow-2xl border border-accent-light animate-in fade-in zoom-in duration-300 flex flex-col">
        {/* Header */}
        <div className="p-8 md:p-12 border-b border-accent-light bg-liza-bg/50">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-serif text-primary">Veelgestelde Vragen</h2>
            <button 
              onClick={onClose}
              className="w-12 h-12 rounded-full flex items-center justify-center border border-accent hover:bg-primary hover:text-white transition-all duration-300 group"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search Input */}
          <div className="relative max-w-2xl">
            <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none text-accent">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Zoek een vraag of onderwerp..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border border-accent-light pl-16 pr-6 py-5 rounded-2xl text-base focus:outline-none focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all font-light shadow-sm"
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-grow overflow-y-auto p-8 md:p-12 space-y-12">
          {filteredFaqs.length > 0 ? (
            <div className="space-y-12">
              {filteredFaqs.map((faq, i) => (
                <div key={i} className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <span className="text-accent font-bold tracking-[0.4em] text-[10px] uppercase mb-4 block">Vraag</span>
                  <h3 className="text-xl font-serif text-primary mb-4 leading-tight">{faq.question}</h3>
                  <div className="prose prose-sm text-gray-600 font-light leading-relaxed">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-20 text-center animate-in fade-in zoom-in duration-500">
              <div className="w-16 h-16 bg-accent-light/30 rounded-full flex items-center justify-center text-accent mx-auto mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 9.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-serif text-primary mb-4">Geen resultaten gevonden</h3>
              <p className="text-gray-500 font-light text-sm max-w-sm mx-auto">
                We konden geen antwoord vinden op "{searchQuery}". Probeer een andere zoekterm of neem direct contact op.
              </p>
            </div>
          )}

          <div className="bg-liza-bg p-8 rounded-3xl border border-accent-light mt-8">
            <h4 className="font-bold text-primary uppercase text-[10px] tracking-widest mb-4">Persoonlijke hulp nodig?</h4>
            <p className="text-xs text-gray-500 font-light leading-relaxed">
              Staat je vraag er niet tussen? Neem gerust contact op via <span className="font-medium text-primary">{CONTACT_INFO.email}</span> of bel naar <span className="font-medium text-primary">{CONTACT_INFO.phone}</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQModal;
